import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Typography,
  OutlinedInput,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';
import {
  useFindUserForUserQuery,
  useUpdateEmailPasswordForUserMutation,
} from '../../api/api-hooks';
import { nullStringCheck } from '../../utils/stringManipulation';
import { getPasswordStrength } from '../auth/authUtils';
import { Messages, PasswordForm } from './settingsTypes';

interface Props {
  messagesState: Messages;
  setMessage: (message: string) => void;
}

const initialFormState: PasswordForm = {
  oldPassword: '',
  newPassword: '',
  showOldPassword: false,
  showNewPassword: false,
};

const ChangePasswordForm = ({ messagesState, setMessage }: Props) => {
  const [updateUser] = useUpdateEmailPasswordForUserMutation();
  const { data: profile } = useFindUserForUserQuery({});
  const [formState, setFormState] = useState(initialFormState);
  const [passwordScore, setPasswordScore] = useState<number>(0);

  if (profile === undefined) return <CircularProgress></CircularProgress>;

  const handler = (event: any) => {
    const { name, value } = event.target;
    setFormState({ ...formState, [name]: value });
    if (name === 'newPassword') {
      import('zxcvbn').then((check) => {
        const { score } = check.default(value);
        setPasswordScore(score);
      });
    }
  };
  const saveEmailPassword = async () => {
    const result = await updateUser({
      changePasswordDto: {
        id: profile.id,
        oldEmail: profile.email,
        newEmail: profile.email,
        ...formState,
      },
    });
    if ('error' in result) setMessage('Invalid attempt.');
    else setMessage('Settings saved.');
  };
  const handleShowOldPassword = () => {
    setFormState({ ...formState, showOldPassword: !formState.showOldPassword });
  };
  const handleShowNewPassword = () => {
    setFormState({ ...formState, showNewPassword: !formState.showNewPassword });
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  return (
    <Grid
      container
      lg={4}
      md={6}
      item
      direction='column'
      alignItems='flex-start'
      spacing={3}
    >
      <Grid item>
        <Typography variant='h6'>Update Password</Typography>
      </Grid>
      <Grid item style={{ width: '100%' }}>
        <FormControl style={{ width: '100%' }} variant='outlined'>
          <InputLabel htmlFor='standard-adornment-password'>
            Current Password
          </InputLabel>
          <OutlinedInput
            id='standard-adornment-password'
            type={formState.showOldPassword ? 'text' : 'password'}
            value={nullStringCheck(formState.oldPassword)}
            onChange={handler}
            name='oldPassword'
            endAdornment={
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={handleShowOldPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge='end'
                  size='large'
                >
                  {formState.showOldPassword ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Grid>
      <Grid item style={{ width: '100%' }}>
        <FormControl style={{ width: '100%' }} variant='outlined'>
          <InputLabel htmlFor='standard-adornment-password'>
            New Password
          </InputLabel>
          <OutlinedInput
            id='standard-adornment-password'
            type={formState.showNewPassword ? 'text' : 'password'}
            value={nullStringCheck(formState.newPassword)}
            onChange={handler}
            name='newPassword'
            endAdornment={
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={handleShowNewPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge='end'
                  size='large'
                >
                  {formState.showNewPassword ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Grid>

      <Grid item style={{ width: '100%' }}>
        {formState.newPassword && (
          <Typography align='left'>
            <strong>Password Strength</strong>
            {`: ${getPasswordStrength(passwordScore)}`}
          </Typography>
        )}
      </Grid>

      <Grid item container wrap='nowrap' alignItems='center' spacing={2}>
        <Grid item>
          <Button
            onClick={saveEmailPassword}
            variant='contained'
            color='primary'
            disabled={
              messagesState.password === 'Settings saved.' ||
              !formState.oldPassword ||
              (!!formState.newPassword && formState.newPassword.length < 8)
            }
          >
            Submit
          </Button>
        </Grid>
        <Grid item>
          {(!formState.oldPassword ||
            (formState.newPassword && formState.newPassword.length < 8)) && (
            <Typography align='left' color='error'>
              Current password is required, and new password must be at least 8
              characters long.
            </Typography>
          )}
        </Grid>
      </Grid>

      <Grid item>
        <Typography>{messagesState.password}</Typography>
      </Grid>
    </Grid>
  );
};

export default ChangePasswordForm;
